import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { publicClientRequirementAnalysisApi } from '../api';
import {
  Loader2,
  FileText,
  Download,
  Trash2,
  CheckCircle2,
  Clock,
  Send,
  AlertCircle,
  Shield,
  ClipboardList,
  Paperclip,
} from 'lucide-react';
import { toast } from 'sonner';
import { extractApiError } from '../utils/dateValidation';

const STATUS_CONFIG = {
  draft: { label: 'Aguardando preenchimento', color: 'bg-gray-500/20 text-gray-400' },
  pending_analysis: { label: 'Enviado · aguardando análise', color: 'bg-yellow-500/20 text-yellow-400' },
  in_analysis: { label: 'Em análise', color: 'bg-blue-500/20 text-blue-400' },
  approved: { label: 'Aprovado', color: 'bg-green-500/20 text-green-400' },
  correction_needed: { label: 'Necessita correção', color: 'bg-red-500/20 text-red-400' },
};

const STEPS = [
  { key: 'fill', label: 'Preenchimento' },
  { key: 'submitted', label: 'Enviado' },
  { key: 'analysis', label: 'Em análise' },
  { key: 'result', label: 'Resultado' },
];

const currentStep = (status) => {
  switch (status) {
    case 'draft': return 0;
    case 'pending_analysis': return 1;
    case 'in_analysis': return 2;
    case 'correction_needed': return 3;
    case 'approved': return 3;
    default: return 0;
  }
};

const SCOPE_OPTIONS = [
  { key: 'logistica_integrada', label: 'Logística Integrada' },
  { key: 'etiquetagem', label: 'Etiquetagem' },
  { key: 'armazenagem', label: 'Armazenagem' },
  { key: 'picking_packing', label: 'Picking/Packing' },
  { key: 'operacao_inhouse', label: 'Operação "In-House"' },
  { key: 'montagem_kits', label: 'Montagem de Kits' },
  { key: 'transportes', label: 'Transportes' },
];

const CHARGE_ITEMS = [
  { key: 'ad_valorem', label: 'Cobrança de Ad. Valorem em relação aos produtos' },
  { key: 'pico_pallets', label: 'Cobrança por pico de pallets' },
  { key: 'pallets_recebidos', label: 'Cobrança por pallets recebidos' },
  { key: 'linhas_atendidas', label: 'Cobrança por linhas atendidas' },
  { key: 'volumes_pallets_expedidos', label: 'Cobrança por volumes/pallets expedidos' },
  { key: 'descarga_veiculos', label: 'Cobrança por descarga de veículos' },
  { key: 'utilizacao_internet', label: 'Cobrança por utilização de internet' },
  { key: 'embalagens_expedicao', label: 'Cobrança por embalagens utilizadas para expedição' },
  { key: 'fitas_adesivas', label: 'Cobrança por fitas adesivas utilizadas' },
  { key: 'etiquetagem', label: 'Cobrança por etiquetagem de produtos/embalagens' },
  { key: 'area_ocupada', label: 'Cobrança por área ocupada (metro quadrado)' },
  { key: 'area_segregada', label: 'Cobrança por área segregada' },
  { key: 'area_refrigerada', label: 'Cobrança por área refrigerada' },
  { key: 'linhas_nf', label: 'Cobrança por linhas de NF' },
  { key: 'pallets_repaletizados', label: 'Cobrança por pallets repaletizados' },
  { key: 'reembalamento', label: 'Cobrança por reembalamento de produtos' },
  { key: 'pesagem_cubagem', label: 'Cobrança para realizar pesagem/cubagem' },
  { key: 'fracionamento_graos', label: 'Cobrança para fracionamento em grãos' },
  { key: 'fracionamento', label: 'Cobrança por fracionamento' },
  { key: 'conferencia_itens', label: 'Cobrança por conferência de itens dentro de caixas' },
  { key: 'filmar_pallets', label: 'Cobrança por filmar pallets' },
  { key: 'lonamento', label: 'Cobrança por lonamento' },
  { key: 'impressao_a4', label: 'Cobrança por impressão de A4' },
  { key: 'pallets_locados', label: 'Cobrança por pallets locados' },
  { key: 'pallet_out', label: 'Cobrança por pallet out' },
];

export const buildRequirementFormData = (item) => ({
  razao_social: item?.razao_social || '',
  cnpj: item?.cnpj || '',
  inscricao_estadual: item?.inscricao_estadual || '',
  endereco: item?.endereco || '',
  bairro: item?.bairro || '',
  cidade: item?.cidade || '',
  uf: item?.uf || '',
  cep: item?.cep || '',
  contato: item?.contato || '',
  email: item?.email || '',
  telefone: item?.telefone || '',
  cargo: item?.cargo || '',
  atividade_principal: item?.atividade_principal || '',
  escalonamento_cliente: item?.escalonamento_cliente || '',
  dimensoes_produto_embalagem: item?.dimensoes_produto_embalagem || '',
  produto_sensivel_restricao: item?.produto_sensivel_restricao || '',
  enviar_book_catalogo: item?.enviar_book_catalogo || '',
  outros_produtos: item?.outros_produtos || '',
  escopo: item?.escopo || [],
  escopo_outros: item?.escopo_outros || '',
  duracao_contrato_anos: item?.duracao_contrato_anos || '',
  data_inicio_operacao: item?.data_inicio_operacao || '',
  previsao_aumento_volume: item?.previsao_aumento_volume || '',
  regime_fiscal: item?.regime_fiscal || '',
  fluxo_fiscal: item?.fluxo_fiscal || '',
  condicoes_temperatura: item?.condicoes_temperatura || '',
  responsabilidade_seguro: item?.responsabilidade_seguro || '',
  recebimento_entrada_min: item?.recebimento_entrada_min || '',
  recebimento_entrada_med: item?.recebimento_entrada_med || '',
  recebimento_entrada_max: item?.recebimento_entrada_max || '',
  forma_envio_operador: item?.forma_envio_operador || '',
  logistica_reversa: item?.logistica_reversa || '',
  recebimentos_frequencia: item?.recebimentos_frequencia || '',
  conferencia_tipo: item?.conferencia_tipo || '',
  registro_fotografico: item?.registro_fotografico || '',
  recebimento_informacoes_adicionais: item?.recebimento_informacoes_adicionais || '',
  quantidade_skus: item?.quantidade_skus || '',
  atividades_armazenagem: item?.atividades_armazenagem || '',
  tipo_palete: item?.tipo_palete || '',
  restricoes_embalagem: item?.restricoes_embalagem || '',
  controle_operacao: item?.controle_operacao || '',
  fispq_fds_armazenagem: item?.fispq_fds_armazenagem || '',
  requisitos_especificos_armazenagem: item?.requisitos_especificos_armazenagem || '',
  restricao_empilhamento: item?.restricao_empilhamento || '',
  area_segregada: item?.area_segregada || '',
  dimensoes_palete: item?.dimensoes_palete || '',
  area_utilizada_atualmente: item?.area_utilizada_atualmente || '',
  inventario_auditoria: item?.inventario_auditoria || '',
  armazenagem_informacoes_adicionais: item?.armazenagem_informacoes_adicionais || '',
  expedicao_saida_min: item?.expedicao_saida_min || '',
  expedicao_saida_med: item?.expedicao_saida_med || '',
  expedicao_saida_max: item?.expedicao_saida_max || '',
  fracionamento_caixa: item?.fracionamento_caixa || '',
  repaletizacao: item?.repaletizacao || '',
  requisitos_especiais_expedicao: item?.requisitos_especiais_expedicao || '',
  expedicao_informacoes_adicionais: item?.expedicao_informacoes_adicionais || '',
  qtd_nf_mensal: item?.qtd_nf_mensal || '',
  qtd_media_pedidos_mensal: item?.qtd_media_pedidos_mensal || '',
  qtd_nf_por_pedido: item?.qtd_nf_por_pedido || '',
  qtd_linhas_por_nf: item?.qtd_linhas_por_nf || '',
  qtd_volumes_por_nf: item?.qtd_volumes_por_nf || '',
  qtd_media_itens_pedido: item?.qtd_media_itens_pedido || '',
  pedidos_emergenciais: item?.pedidos_emergenciais || '',
  operacao_fora_horario: item?.operacao_fora_horario || '',
  codigo_barras_padrao: item?.codigo_barras_padrao || '',
  controle_lote: item?.controle_lote || '',
  sistema_controle_estoque: item?.sistema_controle_estoque || '',
  integracao_sistemas: item?.integracao_sistemas || '',
  sistema_oficial: item?.sistema_oficial || '',
  tipo_rastreabilidade: item?.tipo_rastreabilidade || '',
  anexo_faturamento_descricao: item?.anexo_faturamento_descricao || '',
  anexo_proposta_descricao: item?.anexo_proposta_descricao || '',
  carga_dedicada: item?.carga_dedicada || '',
  tipo_produto_transporte: item?.tipo_produto_transporte || '',
  fispq_fds_transporte: item?.fispq_fds_transporte || '',
  requisitos_especificos_transporte: item?.requisitos_especificos_transporte || '',
  horario_corte: item?.horario_corte || '',
  valor_produtos: item?.valor_produtos || '',
  slas_recebimento_processamento_expedicao: item?.slas_recebimento_processamento_expedicao || '',
  indicadores_kpis: item?.indicadores_kpis || '',
  insumos_necessarios: item?.insumos_necessarios || '',
  responsavel_insumos: item?.responsavel_insumos || '',
  exigencias_seguranca_confidencialidade: item?.exigencias_seguranca_confidencialidade || '',
  cobranca_items: item?.cobranca_items || [],
  observacoes: item?.observacoes || '',
});

export const RequirementAnalysisFormFields = ({ formData, readOnly = false }) => {
  const noop = () => {};
  return (
    <fieldset disabled={readOnly} className="space-y-6 min-w-0">
      {/* 1. Identificação */}
      <div className="space-y-3">
        <SectionTitle>1. Identificação do Cliente</SectionTitle>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Razão Social" value={formData.razao_social} onChange={noop} required />
          <Field label="CNPJ" value={formData.cnpj} onChange={noop} maxLength={255} />
          <Field label="Inscrição Estadual" value={formData.inscricao_estadual} onChange={noop} />
          <Field label="Endereço" value={formData.endereco} onChange={noop} grid={false} />
          <Field label="Bairro" value={formData.bairro} onChange={noop} />
          <Field label="Cidade" value={formData.cidade} onChange={noop} />
          <Field label="UF" value={formData.uf} onChange={noop} maxLength={2} />
          <Field label="CEP" value={formData.cep} onChange={noop} />
          <Field label="Contato" value={formData.contato} onChange={noop} />
          <Field label="E-mail" value={formData.email} onChange={noop} />
          <Field label="Telefone" value={formData.telefone} onChange={noop} />
          <Field label="Cargo" value={formData.cargo} onChange={noop} />
          <Field label="Atividade Principal" value={formData.atividade_principal} onChange={noop} grid={false} />
          <Field label="Escalonamento do Cliente" value={formData.escalonamento_cliente} onChange={noop} grid={false} />
        </div>
      </div>

      {/* 2. Produtos */}
      <div className="space-y-3">
        <SectionTitle>2. Tipos e características dos produtos</SectionTitle>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Dimensões do produto/embalagem" value={formData.dimensoes_produto_embalagem} onChange={noop} />
          <div className="md:col-span-2">
            <SimNaoRow
              label="Produto sensível ou com restrição de manuseio?"
              value={formData.produto_sensivel_restricao}
              onChange={noop}
            />
          </div>
          <div className="md:col-span-2">
            <SimNaoRow
              label="Enviar book, catálogo ou relação dos itens?"
              value={formData.enviar_book_catalogo}
              onChange={noop}
            />
          </div>
          <Field label="Outros (especificar)" value={formData.outros_produtos} onChange={noop} grid={false} />
        </div>
      </div>

      {/* 3. Escopo */}
      <div className="space-y-3">
        <SectionTitle>3. Escopo (assinalar as atividades pertencentes ao escopo)</SectionTitle>
        <div className="px-1">
          <CheckGrid options={SCOPE_OPTIONS} values={formData.escopo} onToggle={noop} />
        </div>
        <Field label="Outros (especificar)" value={formData.escopo_outros} onChange={noop} grid={false} />
      </div>

      {/* 4. Operação */}
      <div className="space-y-3">
        <SectionTitle>4. Características da Operação</SectionTitle>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Duração do Contrato (anos)" value={formData.duracao_contrato_anos} onChange={noop} />
          <Field label="Data prevista para início da operação" type="date" value={formData.data_inicio_operacao} onChange={noop} />
          <Field label="Previsão de aumento de volume? Qual estimativa?" value={formData.previsao_aumento_volume} onChange={noop} />
          <Field label="Regime Fiscal (Armazém geral ou Filial)" value={formData.regime_fiscal} onChange={noop} />
          <Field label="Qual é o fluxo fiscal utilizado: NF, DCE ou outro?" value={formData.fluxo_fiscal} onChange={noop} />
          <Field label="Condições de Temperatura" value={formData.condicoes_temperatura} onChange={noop} />
          <Field label="Responsabilidade do Seguro" value={formData.responsabilidade_seguro} onChange={noop} />
        </div>
      </div>

      {/* 5. Recebimento */}
      <div className="space-y-3">
        <SectionTitle>5. Atividade de Recebimento</SectionTitle>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Field label="Entrada mensal — mínimo" value={formData.recebimento_entrada_min} onChange={noop} />
          <Field label="Entrada mensal — médio" value={formData.recebimento_entrada_med} onChange={noop} />
          <Field label="Entrada mensal — máximo" value={formData.recebimento_entrada_max} onChange={noop} />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Forma de envio ao Operador (carga batida ou paletizada)" value={formData.forma_envio_operador} onChange={noop} />
          <Field label="Logística Reversa (devolução)" value={formData.logistica_reversa} onChange={noop} />
          <Field label="Recebimentos: diários, semanais, mensais ou sob demanda?" value={formData.recebimentos_frequencia} onChange={noop} />
          <Field label="Conferência: 100%, por amostragem, checklist ou outro?" value={formData.conferencia_tipo} onChange={noop} />
          <Field label="Registro fotográfico? Se sim, especifique." value={formData.registro_fotografico} onChange={noop} />
          <Field label="Informações Adicionais" value={formData.recebimento_informacoes_adicionais} onChange={noop} />
        </div>
      </div>

      {/* 6. Armazenagem */}
      <div className="space-y-3">
        <SectionTitle>6. Atividade de Armazenagem</SectionTitle>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Quantidade de SKUs" value={formData.quantidade_skus} onChange={noop} />
          <Field label="Atividades realizadas (montagem de kits, abertura de caixas etc)" value={formData.atividades_armazenagem} onChange={noop} />
          <Field label="Paletes de madeira fumegada, plástico ou metal (deve ser um desses)" value={formData.tipo_palete} onChange={noop} />
          <Field label="Restrições de embalagem (não receberemos insetos, sujidades, mofos, molhado, quebrado, resíduos)" value={formData.restricoes_embalagem} onChange={noop} />
          <Field label="Controle da operação: palete, caixa ou unidade?" value={formData.controle_operacao} onChange={noop} />
          <Field label="FISPQs/FDS do produto (se aplicável)" value={formData.fispq_fds_armazenagem} onChange={noop} />
          <Field label="Requisitos Específicos" value={formData.requisitos_especificos_armazenagem} onChange={noop} />
          <Field label="Possui restrição de empilhamento? Especificar:" value={formData.restricao_empilhamento} onChange={noop} />
          <Field label="Necessária área segregada ou controle de acesso?" value={formData.area_segregada} onChange={noop} />
          <Field label="Dimensões do Palete (comprimento x largura x altura)" value={formData.dimensoes_palete} onChange={noop} />
          <Field label="Área utilizada atualmente" value={formData.area_utilizada_atualmente} onChange={noop} />
          <Field label="Haverá inventário/auditoria? Periodicidade:" value={formData.inventario_auditoria} onChange={noop} />
          <Field label="Informações Adicionais" value={formData.armazenagem_informacoes_adicionais} onChange={noop} grid={false} />
        </div>
      </div>

      {/* 7. Expedição */}
      <div className="space-y-3">
        <SectionTitle>7. Atividade de Expedição</SectionTitle>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Field label="Saída mensal — mínimo" value={formData.expedicao_saida_min} onChange={noop} />
          <Field label="Saída mensal — médio" value={formData.expedicao_saida_med} onChange={noop} />
          <Field label="Saída mensal — máximo" value={formData.expedicao_saida_max} onChange={noop} />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Fracionamento de Caixa (abrem-se caixas na separação? sim/não e %)" value={formData.fracionamento_caixa} onChange={noop} />
          <Field label="É necessária a repaletização?" value={formData.repaletizacao} onChange={noop} />
          <Field label="Requisitos especiais para expedição (pallets, cantoneiras, fitas, caixas, isopor, strech film etc)" value={formData.requisitos_especiais_expedicao} onChange={noop} />
          <Field label="Informações Adicionais" value={formData.expedicao_informacoes_adicionais} onChange={noop} />
        </div>
      </div>

      {/* 8. Processamento de Pedidos */}
      <div className="space-y-3">
        <SectionTitle>8. Processamento de Pedidos</SectionTitle>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Quantidade de Notas Fiscais Emitidas por Mês" value={formData.qtd_nf_mensal} onChange={noop} />
          <Field label="Quantidade média de pedidos por mês" value={formData.qtd_media_pedidos_mensal} onChange={noop} />
          <Field label="Quantidade de Nota fiscal por pedido" value={formData.qtd_nf_por_pedido} onChange={noop} />
          <Field label="Quantidade de linhas por Nota Fiscal" value={formData.qtd_linhas_por_nf} onChange={noop} />
          <Field label="Quantidade de volumes por Nota Fiscal" value={formData.qtd_volumes_por_nf} onChange={noop} />
          <Field label="Quantidade média de itens por pedido" value={formData.qtd_media_itens_pedido} onChange={noop} />
          <Field label="Existem pedidos emergenciais?" value={formData.pedidos_emergenciais} onChange={noop} />
          <Field label="Existe operação fora do horário comercial? Qual o horário?" value={formData.operacao_fora_horario} onChange={noop} />
        </div>
      </div>

      {/* 9. Tecnologia */}
      <div className="space-y-3">
        <SectionTitle>9. Tecnologia</SectionTitle>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Produtos ou Embalagens possuem Código de Barras? (padrão: EAN 13, EAN 128, DUN 14 etc)" value={formData.codigo_barras_padrao} onChange={noop} />
          <Field label="Controle de lote (impresso na embalagem, no produto e na NF de venda?)" value={formData.controle_lote} onChange={noop} />
          <Field label="Qual o Sistema de Controle de Estoque utilizado Atualmente" value={formData.sistema_controle_estoque} onChange={noop} />
          <Field label="Será necessária integração entre sistemas? Qual sistema?" value={formData.integracao_sistemas} onChange={noop} />
          <Field label="Qual será o sistema oficial: Place, cliente ou modelo híbrido?" value={formData.sistema_oficial} onChange={noop} />
          <Field label="Tipo de rastreabilidade: lote, série, IMEI, código interno, foto ou outra?" value={formData.tipo_rastreabilidade} onChange={noop} />
        </div>
      </div>

      {/* 10. Distribuição */}
      <div className="space-y-3">
        <SectionTitle>10. Distribuição</SectionTitle>
        <TextArea
          label="Anexar arquivo em formato txt, somente frete CIF, do faturamento dos últimos 3 meses, contendo: nº da NF, data de emissão, CEP, cidade, estado, quantidade de volumes, peso, valor e meio de transporte — utilize o campo de anexos abaixo"
          value={formData.anexo_faturamento_descricao}
          onChange={noop}
          rows={3}
          grid={false}
        />
      </div>

      {/* 12. Transporte */}
      <div className="space-y-3">
        <SectionTitle>12. Transporte</SectionTitle>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Cliente Necessita de Carga Dedicada" value={formData.carga_dedicada} onChange={noop} />
          <Field label="Tipo de produto a ser transportado" value={formData.tipo_produto_transporte} onChange={noop} />
          <Field label="FISPQs/FDS do produto (se aplicável)" value={formData.fispq_fds_transporte} onChange={noop} />
          <Field label="Requisitos específicos / temperatura?" value={formData.requisitos_especificos_transporte} onChange={noop} />
          <Field label="Horário de corte" value={formData.horario_corte} onChange={noop} />
          <Field label="Valor dos produtos" value={formData.valor_produtos} onChange={noop} />
        </div>
      </div>

      {/* 13. SLA */}
      <div className="space-y-3">
        <SectionTitle>13. SLA</SectionTitle>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <TextArea
            label="Anexar proposta ou documento — utilize o campo de anexos abaixo"
            value={formData.anexo_proposta_descricao}
            onChange={noop}
            rows={2}
          />
          <Field label="Quais os SLAs de recebimento, processamento e expedição?" value={formData.slas_recebimento_processamento_expedicao} onChange={noop} />
          <Field label="Quais indicadores ou KPIs serão acompanhados?" value={formData.indicadores_kpis} onChange={noop} grid={false} />
        </div>
      </div>

      {/* 14. Informações Adicionais */}
      <div className="space-y-3">
        <SectionTitle>14. Informações Adicionais</SectionTitle>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Insumos necessários para a operação" value={formData.insumos_necessarios} onChange={noop} />
          <Field label="Responsável pelos insumos: cliente, Place ou compartilhado" value={formData.responsavel_insumos} onChange={noop} />
          <Field label="Exigências de segurança, confidencialidade ou compliance" value={formData.exigencias_seguranca_confidencialidade} onChange={noop} grid={false} />
        </div>
      </div>

      {/* Checklist de Cobrança */}
      <div className="space-y-3">
        <SectionTitle>Atividades que devem ser cobradas do cliente</SectionTitle>
        <div className="px-1">
          <CheckGrid options={CHARGE_ITEMS} values={formData.cobranca_items} onToggle={noop} />
        </div>
      </div>

      {/* Observações */}
      <div className="space-y-3">
        <SectionTitle>Observações</SectionTitle>
        <TextArea value={formData.observacoes} onChange={noop} rows={3} grid={false} />
      </div>
    </fieldset>
  );
};

const SectionTitle = ({ children }) => (
  <h3 className="text-sm font-bold text-green-400 uppercase tracking-wide bg-gray-800/60 px-3 py-2 rounded mb-3">
    {children}
  </h3>
);

const Field = ({ label, value, onChange, type = 'text', placeholder, grid = true, required = false, maxLength }) => (
  <div className={grid ? 'md:col-span-1' : 'md:col-span-2'}>
    <label className="block text-sm font-medium text-gray-300 mb-2">{label}{required && <span className="text-red-400"> *</span>}</label>
    <input
      type={type}
      value={value}
      onChange={onChange}
      className="input w-full"
      placeholder={placeholder}
      maxLength={maxLength}
    />
  </div>
);

const TextArea = ({ label, value, onChange, rows = 2, placeholder, grid = true }) => (
  <div className={grid ? 'md:col-span-1' : 'md:col-span-2'}>
    <label className="block text-sm font-medium text-gray-300 mb-2">{label}</label>
    <textarea value={value} onChange={onChange} className="input w-full" rows={rows} placeholder={placeholder} />
  </div>
);

const SimNaoRow = ({ label, value, onChange }) => (
  <div className="md:col-span-2 flex flex-wrap items-center gap-x-6 gap-y-2">
    <span className="text-sm text-gray-300 w-full md:w-1/2">{label}</span>
    {[['sim', 'Sim'], ['nao', 'Não']].map(([val, text]) => (
      <label key={val} className="flex items-center gap-1.5 text-xs text-gray-400 cursor-pointer">
        <input
          type="radio"
          checked={value === val}
          onChange={() => onChange(val)}
          className="form-radio text-green-500"
        />
        {text}
      </label>
    ))}
  </div>
);

const CheckGrid = ({ options, values, onToggle }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
    {options.map((opt) => (
      <label key={opt.key} className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
        <input
          type="checkbox"
          checked={values.includes(opt.key)}
          onChange={() => onToggle(opt.key)}
          className="form-checkbox text-green-500"
        />
        {opt.label}
      </label>
    ))}
  </div>
);

const ClientRequirementAnalysisForm = () => {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [formData, setFormData] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [removingId, setRemovingId] = useState(null);
  const [downloadingId, setDownloadingId] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await publicClientRequirementAnalysisApi.get(token);
        setData(res.data);
        setFormData(buildRequirementFormData(res.data));
      } catch (err) {
        setError(true);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [token]);

  const statusCfg = STATUS_CONFIG[data?.status] || STATUS_CONFIG.draft;

  const set = (key) => (e) => setFormData((f) => ({ ...f, [key]: e.target.value }));
  const setVal = (key, value) => setFormData((f) => ({ ...f, [key]: value }));

  const toggleScope = (key) => {
    setFormData((f) => ({
      ...f,
      escopo: f.escopo.includes(key) ? f.escopo.filter((c) => c !== key) : [...f.escopo, key],
    }));
  };

  const toggleCharge = (key) => {
    setFormData((f) => ({
      ...f,
      cobranca_items: f.cobranca_items.includes(key) ? f.cobranca_items.filter((c) => c !== key) : [...f.cobranca_items, key],
    }));
  };

  const handleUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      await publicClientRequirementAnalysisApi.uploadAttachment(token, file);
      toast.success('Documento anexado com sucesso');
      const res = await publicClientRequirementAnalysisApi.get(token);
      setData(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao anexar documento');
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = async (attId) => {
    if (!window.confirm('Remover este documento?')) return;
    setRemovingId(attId);
    try {
      await publicClientRequirementAnalysisApi.deleteAttachment(token, attId);
      toast.success('Documento removido');
      const res = await publicClientRequirementAnalysisApi.get(token);
      setData(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao remover documento');
    } finally {
      setRemovingId(null);
    }
  };

  const handleDownload = async (att) => {
    setDownloadingId(att.id);
    try {
      const res = await publicClientRequirementAnalysisApi.downloadAttachment(token, att.id);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', att.file_name || 'documento.pdf');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao baixar documento');
    } finally {
      setDownloadingId(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.razao_social.trim()) {
      toast.error('Informe a Razão Social (campo obrigatório)');
      return;
    }
    setSubmitting(true);
    try {
      const payload = { ...formData };
      if (!payload.data_inicio_operacao) delete payload.data_inicio_operacao;
      if (!payload.produto_sensivel_restricao) delete payload.produto_sensivel_restricao;
      if (!payload.enviar_book_catalogo) delete payload.enviar_book_catalogo;
      await publicClientRequirementAnalysisApi.submit(token, payload);
      setSubmitted(true);
    } catch (error) {
      toast.error(extractApiError(error, 'Erro ao enviar a análise'));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: '#0B1220' }} className="flex items-center justify-center">
        <Loader2 className="animate-spin text-green-500" size={32} />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div style={{ minHeight: '100vh', background: '#0B1220' }} className="flex items-center justify-center p-6">
        <div className="card p-8 max-w-md w-full text-center">
          <Shield size={40} className="mx-auto text-red-500 mb-3" />
          <h1 className="text-xl font-bold text-white mb-2">Link inválido ou expirado</h1>
          <p className="text-gray-400 text-sm">Se você recebeu este link por e-mail, verifique se copiou o endereço completo.</p>
        </div>
      </div>
    );
  }

  const stepIndex = currentStep(data.status);

  return (
    <div style={{ minHeight: '100vh', background: '#0B1220' }}>
      <div className="mx-auto max-w-5xl px-4 py-8 space-y-6">
        {/* Brand */}
        <div className="flex items-center gap-3 mb-4">
          <div
            className="w-10 h-10 rounded-full flex items-center justify-center"
            style={{ background: 'linear-gradient(135deg, #2563EB, #00D4CB)' }}
          >
            <ClipboardList size={20} style={{ color: '#fff' }} />
          </div>
          <div>
            <p className="text-white font-semibold">QualyX+ SGQ</p>
            <p className="text-gray-400 text-xs">Análise e Crítica dos Requisitos do Cliente</p>
          </div>
        </div>

        {/* Header */}
        <div className="card p-6">
          <div className="flex flex-wrap items-center gap-3 mb-2">
            <span className="font-mono text-green-400">{data.code}</span>
            <span className={`px-2 py-1 rounded-full text-xs ${statusCfg.color}`}>{statusCfg.label}</span>
          </div>
          <h1 className="text-2xl font-bold text-white mb-1">Formulário de Análise e Crítica dos Requisitos do Cliente</h1>
          <p className="text-gray-400 text-sm">
            Preencha os dados solicitados e anexe a documentação indicada.
          </p>
        </div>

        {/* Progress indicator */}
        <div className="card p-5">
          <div className="flex items-center justify-between">
            {STEPS.map((step, idx) => (
              <React.Fragment key={step.key}>
                <div className="flex flex-col items-center gap-1.5 flex-1">
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${
                      idx < stepIndex ? 'bg-green-500/20 text-green-400' :
                      idx === stepIndex ? 'bg-green-500 text-white' :
                      'bg-gray-700 text-gray-400'
                    }`}
                  >
                    {idx < stepIndex ? <CheckCircle2 size={16} /> : idx + 1}
                  </div>
                  <span className={`text-xs ${idx <= stepIndex ? 'text-gray-200' : 'text-gray-500'}`}>
                    {step.label}
                  </span>
                </div>
                {idx < STEPS.length - 1 && (
                  <div className={`flex-1 h-0.5 rounded ${idx < stepIndex ? 'bg-green-500' : 'bg-gray-700'}`} />
                )}
              </React.Fragment>
            ))}
          </div>
          {data.status === 'correction_needed' && (
            <p className="text-xs text-green-400 text-center mt-3">
              A análise retornou para correção — faça os ajustes e envie novamente.
            </p>
          )}
          {data.status === 'approved' && (
            <p className="text-xs text-green-400 text-center mt-3">
              Análise aprovada. Obrigado pelo preenchimento.
            </p>
          )}
        </div>

        {/* Sucesso */}
        {submitted && (
          <div className="p-6 rounded-lg bg-green-500/10 border border-green-500/30 text-center">
            <CheckCircle2 size={40} className="mx-auto text-green-400 mb-2" />
            <h2 className="text-lg font-bold text-white">Análise enviada com sucesso!</h2>
            <p className="text-gray-400 text-sm mt-1">
              Sua solicitação foi registrada e será analisada pela nossa equipe. Você poderá acompanhar correções
              acessando o mesmo link deste e-mail.
            </p>
          </div>
        )}

        {/* Correção pendente */}
        {data.status === 'correction_needed' && data.admin_observation && (
          <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/30">
            <p className="text-red-400 font-medium flex items-center gap-2 mb-1">
              <AlertCircle size={16} /> Correção solicitada pela nossa equipe
            </p>
            <p className="text-sm text-gray-300 whitespace-pre-wrap">{data.admin_observation}</p>
          </div>
        )}

        {/* Formulário */}
        {data.status !== 'approved' && !submitted && (
          <form onSubmit={handleSubmit} className="card p-6 space-y-6">
            {/* 1. Identificação */}
            <div className="space-y-3">
              <SectionTitle>1. Identificação do Cliente</SectionTitle>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Razão Social" value={formData.razao_social} onChange={set('razao_social')} required />
                <Field label="CNPJ" value={formData.cnpj} onChange={set('cnpj')} maxLength={255} />
                <Field label="Inscrição Estadual" value={formData.inscricao_estadual} onChange={set('inscricao_estadual')} />
                <Field label="Endereço" value={formData.endereco} onChange={set('endereco')} grid={false} />
                <Field label="Bairro" value={formData.bairro} onChange={set('bairro')} />
                <Field label="Cidade" value={formData.cidade} onChange={set('cidade')} />
                <Field label="UF" value={formData.uf} onChange={set('uf')} maxLength={2} />
                <Field label="CEP" value={formData.cep} onChange={set('cep')} />
                <Field label="Contato" value={formData.contato} onChange={set('contato')} />
                <Field label="E-mail" value={formData.email} onChange={set('email')} />
                <Field label="Telefone" value={formData.telefone} onChange={set('telefone')} />
                <Field label="Cargo" value={formData.cargo} onChange={set('cargo')} />
                <Field label="Atividade Principal" value={formData.atividade_principal} onChange={set('atividade_principal')} grid={false} />
                <Field label="Escalonamento do Cliente" value={formData.escalonamento_cliente} onChange={set('escalonamento_cliente')} grid={false} />
              </div>
            </div>

            {/* 2. Produtos */}
            <div className="space-y-3">
              <SectionTitle>2. Tipos e características dos produtos</SectionTitle>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Dimensões do produto/embalagem" value={formData.dimensoes_produto_embalagem} onChange={set('dimensoes_produto_embalagem')} />
                <div className="md:col-span-2">
                  <SimNaoRow
                    label="Produto sensível ou com restrição de manuseio?"
                    value={formData.produto_sensivel_restricao}
                    onChange={(v) => setVal('produto_sensivel_restricao', v)}
                  />
                </div>
                <div className="md:col-span-2">
                  <SimNaoRow
                    label="Enviar book, catálogo ou relação dos itens?"
                    value={formData.enviar_book_catalogo}
                    onChange={(v) => setVal('enviar_book_catalogo', v)}
                  />
                </div>
                <Field label="Outros (especificar)" value={formData.outros_produtos} onChange={set('outros_produtos')} grid={false} />
              </div>
            </div>

            {/* 3. Escopo */}
            <div className="space-y-3">
              <SectionTitle>3. Escopo (assinalar as atividades pertencentes ao escopo)</SectionTitle>
              <div className="px-1">
                <CheckGrid options={SCOPE_OPTIONS} values={formData.escopo} onToggle={toggleScope} />
              </div>
              <Field label="Outros (especificar)" value={formData.escopo_outros} onChange={set('escopo_outros')} grid={false} />
            </div>

            {/* 4. Operação */}
            <div className="space-y-3">
              <SectionTitle>4. Características da Operação</SectionTitle>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Duração do Contrato (anos)" value={formData.duracao_contrato_anos} onChange={set('duracao_contrato_anos')} />
                <Field label="Data prevista para início da operação" type="date" value={formData.data_inicio_operacao} onChange={set('data_inicio_operacao')} />
                <Field label="Previsão de aumento de volume? Qual estimativa?" value={formData.previsao_aumento_volume} onChange={set('previsao_aumento_volume')} />
                <Field label="Regime Fiscal (Armazém geral ou Filial)" value={formData.regime_fiscal} onChange={set('regime_fiscal')} />
                <Field label="Qual é o fluxo fiscal utilizado: NF, DCE ou outro?" value={formData.fluxo_fiscal} onChange={set('fluxo_fiscal')} />
                <Field label="Condições de Temperatura" value={formData.condicoes_temperatura} onChange={set('condicoes_temperatura')} />
                <Field label="Responsabilidade do Seguro" value={formData.responsabilidade_seguro} onChange={set('responsabilidade_seguro')} />
              </div>
            </div>

            {/* 5. Recebimento */}
            <div className="space-y-3">
              <SectionTitle>5. Atividade de Recebimento</SectionTitle>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Field label="Entrada mensal — mínimo" value={formData.recebimento_entrada_min} onChange={set('recebimento_entrada_min')} />
                <Field label="Entrada mensal — médio" value={formData.recebimento_entrada_med} onChange={set('recebimento_entrada_med')} />
                <Field label="Entrada mensal — máximo" value={formData.recebimento_entrada_max} onChange={set('recebimento_entrada_max')} />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Forma de envio ao Operador (carga batida ou paletizada)" value={formData.forma_envio_operador} onChange={set('forma_envio_operador')} />
                <Field label="Logística Reversa (devolução)" value={formData.logistica_reversa} onChange={set('logistica_reversa')} />
                <Field label="Recebimentos: diários, semanais, mensais ou sob demanda?" value={formData.recebimentos_frequencia} onChange={set('recebimentos_frequencia')} />
                <Field label="Conferência: 100%, por amostragem, checklist ou outro?" value={formData.conferencia_tipo} onChange={set('conferencia_tipo')} />
                <Field label="Registro fotográfico? Se sim, especifique." value={formData.registro_fotografico} onChange={set('registro_fotografico')} />
                <Field label="Informações Adicionais" value={formData.recebimento_informacoes_adicionais} onChange={set('recebimento_informacoes_adicionais')} />
              </div>
            </div>

            {/* 6. Armazenagem */}
            <div className="space-y-3">
              <SectionTitle>6. Atividade de Armazenagem</SectionTitle>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Quantidade de SKUs" value={formData.quantidade_skus} onChange={set('quantidade_skus')} />
                <Field label="Atividades realizadas (montagem de kits, abertura de caixas etc)" value={formData.atividades_armazenagem} onChange={set('atividades_armazenagem')} />
                <Field label="Paletes de madeira fumegada, plástico ou metal (deve ser um desses)" value={formData.tipo_palete} onChange={set('tipo_palete')} />
                <Field label="Restrições de embalagem (não receberemos insetos, sujidades, mofos, molhado, quebrado, resíduos)" value={formData.restricoes_embalagem} onChange={set('restricoes_embalagem')} />
                <Field label="Controle da operação: palete, caixa ou unidade?" value={formData.controle_operacao} onChange={set('controle_operacao')} />
                <Field label="FISPQs/FDS do produto (se aplicável)" value={formData.fispq_fds_armazenagem} onChange={set('fispq_fds_armazenagem')} />
                <Field label="Requisitos Específicos" value={formData.requisitos_especificos_armazenagem} onChange={set('requisitos_especificos_armazenagem')} />
                <Field label="Possui restrição de empilhamento? Especificar:" value={formData.restricao_empilhamento} onChange={set('restricao_empilhamento')} />
                <Field label="Necessária área segregada ou controle de acesso?" value={formData.area_segregada} onChange={set('area_segregada')} />
                <Field label="Dimensões do Palete (comprimento x largura x altura)" value={formData.dimensoes_palete} onChange={set('dimensoes_palete')} />
                <Field label="Área utilizada atualmente" value={formData.area_utilizada_atualmente} onChange={set('area_utilizada_atualmente')} />
                <Field label="Haverá inventário/auditoria? Periodicidade:" value={formData.inventario_auditoria} onChange={set('inventario_auditoria')} />
                <Field label="Informações Adicionais" value={formData.armazenagem_informacoes_adicionais} onChange={set('armazenagem_informacoes_adicionais')} grid={false} />
              </div>
            </div>

            {/* 7. Expedição */}
            <div className="space-y-3">
              <SectionTitle>7. Atividade de Expedição</SectionTitle>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Field label="Saída mensal — mínimo" value={formData.expedicao_saida_min} onChange={set('expedicao_saida_min')} />
                <Field label="Saída mensal — médio" value={formData.expedicao_saida_med} onChange={set('expedicao_saida_med')} />
                <Field label="Saída mensal — máximo" value={formData.expedicao_saida_max} onChange={set('expedicao_saida_max')} />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Fracionamento de Caixa (abrem-se caixas na separação? sim/não e %)" value={formData.fracionamento_caixa} onChange={set('fracionamento_caixa')} />
                <Field label="É necessária a repaletização?" value={formData.repaletizacao} onChange={set('repaletizacao')} />
                <Field label="Requisitos especiais para expedição (pallets, cantoneiras, fitas, caixas, isopor, strech film etc)" value={formData.requisitos_especiais_expedicao} onChange={set('requisitos_especiais_expedicao')} />
                <Field label="Informações Adicionais" value={formData.expedicao_informacoes_adicionais} onChange={set('expedicao_informacoes_adicionais')} />
              </div>
            </div>

            {/* 8. Processamento de Pedidos */}
            <div className="space-y-3">
              <SectionTitle>8. Processamento de Pedidos</SectionTitle>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Quantidade de Notas Fiscais Emitidas por Mês" value={formData.qtd_nf_mensal} onChange={set('qtd_nf_mensal')} />
                <Field label="Quantidade média de pedidos por mês" value={formData.qtd_media_pedidos_mensal} onChange={set('qtd_media_pedidos_mensal')} />
                <Field label="Quantidade de Nota fiscal por pedido" value={formData.qtd_nf_por_pedido} onChange={set('qtd_nf_por_pedido')} />
                <Field label="Quantidade de linhas por Nota Fiscal" value={formData.qtd_linhas_por_nf} onChange={set('qtd_linhas_por_nf')} />
                <Field label="Quantidade de volumes por Nota Fiscal" value={formData.qtd_volumes_por_nf} onChange={set('qtd_volumes_por_nf')} />
                <Field label="Quantidade média de itens por pedido" value={formData.qtd_media_itens_pedido} onChange={set('qtd_media_itens_pedido')} />
                <Field label="Existem pedidos emergenciais?" value={formData.pedidos_emergenciais} onChange={set('pedidos_emergenciais')} />
                <Field label="Existe operação fora do horário comercial? Qual o horário?" value={formData.operacao_fora_horario} onChange={set('operacao_fora_horario')} />
              </div>
            </div>

            {/* 9. Tecnologia */}
            <div className="space-y-3">
              <SectionTitle>9. Tecnologia</SectionTitle>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Produtos ou Embalagens possuem Código de Barras? (padrão: EAN 13, EAN 128, DUN 14 etc)" value={formData.codigo_barras_padrao} onChange={set('codigo_barras_padrao')} />
                <Field label="Controle de lote (impresso na embalagem, no produto e na NF de venda?)" value={formData.controle_lote} onChange={set('controle_lote')} />
                <Field label="Qual o Sistema de Controle de Estoque utilizado Atualmente" value={formData.sistema_controle_estoque} onChange={set('sistema_controle_estoque')} />
                <Field label="Será necessária integração entre sistemas? Qual sistema?" value={formData.integracao_sistemas} onChange={set('integracao_sistemas')} />
                <Field label="Qual será o sistema oficial: Place, cliente ou modelo híbrido?" value={formData.sistema_oficial} onChange={set('sistema_oficial')} />
                <Field label="Tipo de rastreabilidade: lote, série, IMEI, código interno, foto ou outra?" value={formData.tipo_rastreabilidade} onChange={set('tipo_rastreabilidade')} />
              </div>
            </div>

            {/* 10. Distribuição */}
            <div className="space-y-3">
              <SectionTitle>10. Distribuição</SectionTitle>
              <TextArea
                label="Anexar arquivo em formato txt, somente frete CIF, do faturamento dos últimos 3 meses, contendo: nº da NF, data de emissão, CEP, cidade, estado, quantidade de volumes, peso, valor e meio de transporte — utilize o campo de anexos abaixo"
                value={formData.anexo_faturamento_descricao}
                onChange={set('anexo_faturamento_descricao')}
                rows={3}
                grid={false}
              />
            </div>

            {/* 12. Transporte */}
            <div className="space-y-3">
              <SectionTitle>12. Transporte</SectionTitle>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Cliente Necessita de Carga Dedicada" value={formData.carga_dedicada} onChange={set('carga_dedicada')} />
                <Field label="Tipo de produto a ser transportado" value={formData.tipo_produto_transporte} onChange={set('tipo_produto_transporte')} />
                <Field label="FISPQs/FDS do produto (se aplicável)" value={formData.fispq_fds_transporte} onChange={set('fispq_fds_transporte')} />
                <Field label="Requisitos específicos / temperatura?" value={formData.requisitos_especificos_transporte} onChange={set('requisitos_especificos_transporte')} />
                <Field label="Horário de corte" value={formData.horario_corte} onChange={set('horario_corte')} />
                <Field label="Valor dos produtos" value={formData.valor_produtos} onChange={set('valor_produtos')} />
              </div>
            </div>

            {/* 13. SLA */}
            <div className="space-y-3">
              <SectionTitle>13. SLA</SectionTitle>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <TextArea
                  label="Anexar proposta ou documento — utilize o campo de anexos abaixo"
                  value={formData.anexo_proposta_descricao}
                  onChange={set('anexo_proposta_descricao')}
                  rows={2}
                />
                <Field label="Quais os SLAs de recebimento, processamento e expedição?" value={formData.slas_recebimento_processamento_expedicao} onChange={set('slas_recebimento_processamento_expedicao')} />
                <Field label="Quais indicadores ou KPIs serão acompanhados?" value={formData.indicadores_kpis} onChange={set('indicadores_kpis')} grid={false} />
              </div>
            </div>

            {/* 14. Informações Adicionais */}
            <div className="space-y-3">
              <SectionTitle>14. Informações Adicionais</SectionTitle>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Insumos necessários para a operação" value={formData.insumos_necessarios} onChange={set('insumos_necessarios')} />
                <Field label="Responsável pelos insumos: cliente, Place ou compartilhado" value={formData.responsavel_insumos} onChange={set('responsavel_insumos')} />
                <Field label="Exigências de segurança, confidencialidade ou compliance" value={formData.exigencias_seguranca_confidencialidade} onChange={set('exigencias_seguranca_confidencialidade')} grid={false} />
              </div>
            </div>

            {/* Checklist de Cobrança */}
            <div className="space-y-3">
              <SectionTitle>Atividades que devem ser cobradas do cliente</SectionTitle>
              <div className="px-1">
                <CheckGrid options={CHARGE_ITEMS} values={formData.cobranca_items} onToggle={toggleCharge} />
              </div>
            </div>

            {/* Observações */}
            <div className="space-y-3">
              <SectionTitle>Observações</SectionTitle>
              <TextArea value={formData.observacoes} onChange={set('observacoes')} rows={3} grid={false} />
            </div>

            {/* Documentos anexados */}
            <div>
              <h3 className="text-sm font-bold text-green-400 uppercase tracking-wide bg-gray-800/60 px-3 py-2 rounded mb-3 flex items-center gap-2">
                <Paperclip size={16} /> Documentos / Anexos
              </h3>
              <p className="text-xs text-gray-500 mb-3">
                Anexe o arquivo txt do faturamento (item 10), a proposta/documento de SLA (item 13), o book/catálogo e demais documentos solicitados.
              </p>

              {(data.attachments || []).length > 0 && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-3">
                  {data.attachments.map((att) => (
                    <div key={att.id} className="flex items-center justify-between gap-2 border border-gray-700 rounded-lg px-3 py-2 bg-gray-800/40">
                      <div className="flex items-center gap-2 min-w-0">
                        <FileText size={16} className="text-green-500 flex-shrink-0" />
                        <div className="min-w-0">
                          <p className="text-sm text-gray-200 truncate">{att.file_name}</p>
                          <p className="text-xs text-gray-500">
                            {att.file_size ? `${(att.file_size / 1024).toFixed(1)} KB` : ''}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <button
                          type="button"
                          onClick={() => handleDownload(att)}
                          className="p-1.5 text-blue-400 hover:text-blue-300"
                          title="Baixar"
                        >
                          {downloadingId === att.id ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
                        </button>
                        <button
                          type="button"
                          onClick={() => handleRemove(att.id)}
                          className="p-1.5 text-red-400 hover:text-red-300"
                          title="Remover"
                        >
                          {removingId === att.id ? <Loader2 size={16} className="animate-spin" /> : <Trash2 size={16} />}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <label
                className={`flex items-center justify-center gap-2 w-full border-2 border-dashed border-gray-600 rounded-lg p-4 text-sm text-gray-400 hover:text-green-400 hover:border-green-500/50 cursor-pointer transition-colors ${uploading ? 'opacity-50 pointer-events-none' : ''}`}
              >
                {uploading ? <Loader2 className="animate-spin text-green-500" size={18} /> : <Paperclip size={18} />}
                {uploading ? 'Enviando documento...' : 'Clique para anexar um documento'}
                <input
                  type="file"
                  accept=".pdf,.png,.jpg,.jpeg,.doc,.docx,.xls,.xlsx,.txt,.csv"
                  className="hidden"
                  disabled={uploading}
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    e.target.value = '';
                    if (f) handleUpload(f);
                  }}
                />
              </label>
            </div>

            {/* Enviar */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 pt-4 border-t border-gray-700">
              <p className="text-sm text-gray-500 flex-1">
                <Clock size={14} className="inline mr-1" />
                Ao enviar, a análise será avaliada pela nossa equipe.
              </p>
              <button type="submit" className="btn-primary flex items-center gap-2 justify-center" disabled={submitting}>
                {submitting ? <Loader2 className="animate-spin" size={20} /> : <Send size={20} />}
                Enviar análise
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default ClientRequirementAnalysisForm;